import { CalculationRowType, VatEntry, VatRate } from "../../model";
import { EnumerationsLogic, Translations, ExpensesVatRate } from "../../logic";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { ExpensesUsecase } from "../ExpensesUsecase";
import { UsecaseRow } from "../UsecaseRow";
import { Numeric, Years, Dates, DatelyObject } from "../../util";

/**
 * Client-side usecases logic for Expenses as well as PrepaidExpenses.
 */
export class ExpensesUsecaseLogic extends BaseUsecaseLogic {
  private _hasGrossVatEntries: boolean;

  private _row: UsecaseRow<CalculationRowType.Expenses, ExpensesUsecase>;
  private _date: DatelyObject;

  /** When user is editing rows (varRate = "multiple"), this is the filed that is being edited => source for calculation. */
  public multipleRatesEditField : "tax" | "gross" | "net" = "gross";

  /** Returns true if the row has gross vatEntries */
  public get hasGrossVatEntries(): boolean {
    return this._hasGrossVatEntries ?? false;
  }

  /**
   * Updates the usecase values based on user input without going to the server.
   */
  public updateUsecase(row: UsecaseRow<CalculationRowType.Expenses, ExpensesUsecase>, date: DatelyObject): void {
    row = row ?? this._row;
    this._row = row;
    date = date ?? this._date;
    this._date = date;

    const vatRate = row.data.vatRate;
    if (vatRate == ExpensesVatRate.Other) {
      if (row.accounting?.vatPercent == null) {
        row.accounting ??= {};
        row.accounting.vatPercent = Years.getYearlyChangingNumbers(date ?? Dates.getToday())?.sideCosts?.palkkausVatPercent;
      }
      this.taxRates["other"] = row.accounting.vatPercent;
    } else {
      row.accounting ??= {};
      row.accounting.vatPercent = undefined;
      this.taxRates["other"] = undefined;
    }

    const taxPercent = this.taxRates[vatRate];
    if (taxPercent != null) {
      row.accounting.vatEntries = [this.getRow( row.count && row.price ? row.count * row.price : undefined, vatRate)];
      row.data.total = undefined;
    } else if (vatRate == ExpensesVatRate.Multiple) {
      if (!row.accounting.vatEntries) {
        row.accounting.vatEntries = this.getEmptyVatEntries();
      } else {
        //UI sends full array of VAT entries, but API wrapper
        //send likely only rows that have some gross value.
        //UI can handle only full array, so this code fills missing empty cells.
        const entries = this.getEmptyVatEntries();
        row.accounting.vatEntries.forEach((r) => {
          //VAT rates have their place on the UI. 24% is first.
          switch (r.vatRate) {
            case VatRate.Vat2400:
              entries[0] = r;
              break;
            case VatRate.Vat1400:
              entries[1] = r;
              break;
            case VatRate.Vat1000:
              entries[2] = r;
              break;
            case VatRate.Vat0000:
              entries[3] = r;
              break;
            case VatRate.Vat2550:
              entries[4] = r;
              break;
            case VatRate.Vat1350:
              entries[5] = r;
              break;
          }
        });
        row.accounting ??= {};
        row.accounting.vatEntries = entries;
      }


      const totalRow: VatEntry = {
        gross: 0,
        net: 0,
        tax: 0,
        taxPercent: 0,
        vatRate: VatRate.Undefined,
      };
      row.accounting?.vatEntries?.forEach((r) => {
        switch (this.multipleRatesEditField) {
          case "net":
            totalRow.net! += r.net = (r.net ?  Numeric.round(r.net) : undefined) ?? 0;
            totalRow.tax! += (r.tax = r.net && r.taxPercent ? Numeric.round(r.net * r.taxPercent) : undefined) ?? 0;
            totalRow.gross! += r.gross = (r.net ?? 0 + (r.tax ?? 0));
            break;
          case "tax":
            totalRow.tax! += r.tax = (r.tax ? Numeric.round(r.tax) : undefined) ?? 0;
            totalRow.net! += r.net = Numeric.round((r.tax ?? 0) / (r.taxPercent ?? 0)) ?? 0;
            totalRow.gross! += r.gross = r.net + r.tax;
            break;
          case "gross":
          default:
            totalRow.gross! += r.gross = Numeric.round(r.gross ?? 0) ?? 0;
            totalRow.net! += r.net = Numeric.round(r.gross / (1 + (r.taxPercent ?? 0))) ?? 0;
            totalRow.tax! += r.tax = (r.gross ?? 0) - (r.net ?? 0);
            break;
        }
      });
      totalRow.taxPercent = Numeric.round((totalRow.tax ?? 0) / (totalRow.net ?? 0));
      row.data.total = totalRow;
    }
    if(row.data.vatRate === ExpensesVatRate.Multiple) {
      this._hasGrossVatEntries = !!row.accounting.vatEntries?.find((x) => x.gross);
    }
  }

  private getEmptyVatEntries(): VatEntry[] {
    return [
      this.getRow(undefined, ExpensesVatRate.Tax24),
      this.getRow(undefined, ExpensesVatRate.Tax14),
      this.getRow(undefined, ExpensesVatRate.Tax10),
      this.getRow(undefined, ExpensesVatRate.Tax0),
      this.getRow(undefined, ExpensesVatRate.Tax255),
      this.getRow(undefined, ExpensesVatRate.Tax135),
    ];
  }



  private getRow(gross: number | undefined, vatRate: ExpensesVatRate): VatEntry {
    if (gross) {
      gross = Numeric.round(gross);
    }
    const taxPercent = this.taxRates[vatRate] || 0;
    const net = gross ? Numeric.round(gross / (1 + taxPercent)) : undefined;

    const taxRateMapper = {
      tax0: VatRate.Vat0000,
      tax10: VatRate.Vat1000,
      tax135: VatRate.Vat1350,
      tax14: VatRate.Vat1400,
      tax24: VatRate.Vat2400,
      tax255: VatRate.Vat2550,
      none: VatRate.None,
      other: VatRate.Other,
    };

    return {
      gross,
      tax: gross && net ? Numeric.round(gross - net) : undefined,
      net: net,
      taxPercent,
      vatRate: taxRateMapper[vatRate] ?? VatRate.None,
    };
  }

  /** Taxrates and their number percentages as a key-value pair. Values are decimal: 0-1 as 0-100 percent. */
  public taxRates: Record<string, number | undefined | null> = {
    tax0: 0,
    tax10: 0.10,
    tax135: 0.135,
    tax14: 0.14,
    tax24: 0.24,
    tax255: 0.255,
    none: 0,
    other: undefined,
  };

  /** Gets validation message for BoardUsecaseLogic  */
  public getUcValidation(row: UsecaseRow<CalculationRowType.Expenses, ExpensesUsecase>): UsecaseValidationMessage {
    let msg = "SALAXY.VALIDATION.Usecases.expensesValidation.noVat";
    switch (row.data.vatRate) {
      case ExpensesVatRate.Tax0:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat0";
        break;
      case ExpensesVatRate.Tax10:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat10";
        break;
      case ExpensesVatRate.Tax135:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat135";
        break;
      case ExpensesVatRate.Tax14:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat14";
        break;
      case ExpensesVatRate.Tax24:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat24";
        break;
      case ExpensesVatRate.Tax255:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat255";
        break;
      case ExpensesVatRate.Multiple:
        msg = EnumerationsLogic.getEnumLabel("ExpensesVatRate", ExpensesVatRate.Multiple);
        break;
      case ExpensesVatRate.Other:
        msg = Translations.get("SALAXY.VALIDATION.Usecases.expensesValidation.vatCustom") + (100 * (row.accounting?.vatPercent ?? 0)) + " %";
        break;
    }
    return { type: "default", msg };
  }
}
